import { useCallback, useContext, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
  getMasterPortfolioV3,
  getVennDemoPortfolio,
  setAsMasterPortfolioV3,
  updatePortfolioV3,
  type Portfolio,
} from 'venn-api';
import { PortfoliosContext, type PortfoliosContextValue } from 'venn-components';
import { proxySetIdState } from 'venn-state';

export const usePortfoliosContext = () => {
  const proxySetId = useRecoilValue(proxySetIdState);
  const setDemoPortfolio = useCallback(async () => {
    try {
      const { content } = await getVennDemoPortfolio(proxySetId);
      setPortfoliosContext((c) => ({
        ...c,
        demoPortfolio: content,
      }));
    } catch {
      setPortfoliosContext((c) => ({
        ...c,
        demoPortfolio: undefined,
      }));
    }
  }, [proxySetId]);

  const setMasterPortfolio = useCallback(async () => {
    try {
      const { content } = await getMasterPortfolioV3(proxySetId);
      setPortfoliosContext((c) => ({
        ...c,
        masterPortfolio: content,
        failedToFetchMasterError: undefined,
      }));
    } catch (e) {
      // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
      const { content } = await e;
      setPortfoliosContext((c) => ({
        ...c,
        masterPortfolio: undefined,
        failedToFetchMasterError: content?.text,
      }));
    }
  }, [proxySetId]);
  const refreshPortfolios = useCallback(async () => {
    try {
      setPortfoliosContext((c) => ({
        ...c,
        loading: true,
      }));
      await Promise.all([setMasterPortfolio(), setDemoPortfolio()]);
    } catch {
      // Do nothing
    }
    setPortfoliosContext((c) => ({
      ...c,
      loading: false,
    }));
  }, [setDemoPortfolio, setMasterPortfolio]);
  const setMasterPortfolioById = useCallback(
    async (masterPortfolioId: number) => {
      setPortfoliosContext((c) => ({
        ...c,
        loading: true,
      }));
      await setAsMasterPortfolioV3(masterPortfolioId, proxySetId);
      await refreshPortfolios();
    },
    [proxySetId, refreshPortfolios],
  );

  const updateMaster = useCallback(
    async (master: Portfolio): Promise<Portfolio> => {
      setPortfoliosContext((c) => ({
        ...c,
        loading: true,
      }));
      const { content: masterPortfolio } = await updatePortfolioV3(master.id, proxySetId, master);
      setPortfoliosContext((c) => ({
        ...c,
        masterPortfolio,
        loading: false,
      }));

      return masterPortfolio;
    },
    [proxySetId],
  );
  const [portfoliosContext, setPortfoliosContext] = useState<PortfoliosContextValue>({
    ...useContext(PortfoliosContext),
    setAsMaster: setMasterPortfolioById,
    updateMaster,
    refresh: refreshPortfolios,
    loading: true,
  });
  useEffect(() => {
    refreshPortfolios();
  }, [refreshPortfolios]);
  return portfoliosContext;
};
